import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Popconfirm } from 'antd'; 
import { ToastContainer, toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8011';

const CategoryDetail = () => {
  const { project, category } = useParams(); // Get project and category from the URL
  const [details, setDetails] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null); // State for the selected image
  const [isModalOpen, setIsModalOpen] = useState(false); // State for the modal
  const placeholderImage = 'https://via.placeholder.com/150'; // Placeholder image URL
  const imgRef = useRef(null); // Ref for the image element
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentExpense, setCurrentExpense] = useState(null);
  const [imageFile, setImageFile] = useState(null);

 
   const deleteExpenseById = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      const response = await fetch(`${API_BASE_URL}/expenses/deleteExpense/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete expense');
      toast.success('Expense deleted successfully');
      // Refresh the expense list after deletion
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const openEditModal = (expense) => {
    setCurrentExpense(expense);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentExpense(null);
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const uploadImage = async (image) => {
    const data = new FormData();
    data.append('file', image);
    data.append('upload_preset', 'DecentBuilders');
    try {
      const response = await fetch('https://api.cloudinary.com/v1_1/dwbwxh3sj/image/upload', {
        method: 'POST',
        body: data,
      });
      const urlData = await response.json();
      return urlData.url;
    } catch (error) {
      console.error(error);
      toast.error('Image upload failed.');
      return null;
    }
  };

const updateExpense = async () => {
try {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token found');
  
  let imageUrl = currentExpense.imagePath; // Use existing image if not changed

  // If new image is uploaded, upload it to Cloudinary first
  if (imageFile) {
    const uploadedImageUrl = await uploadImage(imageFile);
    if (uploadedImageUrl) {
      imageUrl = uploadedImageUrl;
    } else {
      throw new Error('Failed to upload image');
    }
  }

  const updatedExpenseData = {
    title: currentExpense.title,
    description: currentExpense.description,
    price: currentExpense.price,
    category: currentExpense.category,
    project: currentExpense.project,
    date: currentExpense.date,
    imagePath: imageUrl // Updated image URL
  };

  const response = await fetch(`${API_BASE_URL}/expenses/updateExpense/${currentExpense._id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updatedExpenseData),
  });

  if (!response.ok) throw new Error('Failed to update expense');
  
  toast.success('Expense updated successfully');
  setIsEditModalOpen(false);
  closeModal();
  fetchExpenses(); // Refresh expenses after update

} catch (error) {
  console.error('Error updating expense:', error);
  toast.error('Failed to update expense');
}
};

    const fetchExpenses = useCallback(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/expenses/getAllExpenses`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch expenses');
        }

        const data = await response.json();
        setAllExpenses(data);
      } catch (error) {
        console.error('Error fetching expenses:', error);
      }
    }, []);

    useEffect(() => {
      fetchExpenses();
    }, [fetchExpenses]);

  useEffect(() => {
    // Filter expenses based on both the project and category
    const filteredDetails = allExpenses.filter(
      expense => expense.project === project && expense.category === category
    );
    setDetails(filteredDetails);
  }, [project, category, allExpenses]);

  const openModal = (image) => {
    setSelectedImage(image);
    console.log("Opening image:", image); 
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  // const handleImageClick = (e) => {
  //   e.stopPropagation(); // Prevent the click event from bubbling up to the modal
  // };

  const handleMouseDown = (e) => {
    e.preventDefault();
    const img = imgRef.current;
    if (!img) return;

    let startX = e.clientX;
    let startY = e.clientY;
    const initialTransform = img.style.transform || 'translate(0px, 0px)';
    let initialTranslateX = 0;
    let initialTranslateY = 0;

    // Extract initial translation values
    const translateMatch = initialTransform.match(/translate\(([^)]+)\)/);
    if (translateMatch) {
      [initialTranslateX, initialTranslateY] = translateMatch[1].split(',').map(value => parseFloat(value));
    }

    const onMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      img.style.transform = `scale(2) translate(${initialTranslateX + deltaX}px, ${initialTranslateY + deltaY}px)`;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Calculate total price for the category and project
  const totalPrice = details.reduce((acc, expense) => acc + expense.price, 0).toFixed(2);

  return (
    <div className="rounded-lg h-full">
      <div className='bg-white p-3'>
        <button onClick={() => navigate(-1)} className=" sm:mb-0 sm:text-left"> {/* Navigate to the previous page */}
          <i className="fa-solid fa-arrow-left text-xl me-2"></i>
        </button>
      </div>
      <div className='flex flex-col sm:flex-row justify-between items-center text-xl font-semibold bg-themeColor2 p-3 rounded-lg text-themeColor1 m-2'>
        <h2 className="text-xl font-semibold text-black text-center">
          {category} in {decodeURIComponent(project)}
        </h2>
        <h2 className="text-xl font-semibold bg-themeColor1 text-themeColor2 px-2 py-1 rounded-lg">
          Total: Rs. {totalPrice}
        </h2>
      </div>
      <div className=' overflow-y-auto scrollbar-hidden m-2'>
        {details.length === 0 ? (
          <p>No expenses found for this category in the selected project.</p>
        ) : (
          details.map((expense, index) => (
            <div className="flex flex-col sm:flex-row sm:gap-4 mb-2 border border-gray-300 rounded-lg p-4 bg-gray-50" key={index}>
              <div className=" flex-1">
                <div className='flex justify-between'>
                  <h3 className="text-lg font-semibold text-themeColor">{expense.title}</h3>
                  <div className="px-6 pt-2 whitespace-nowrap text-sm text-themeColor flex gap-2">
                    <i className="fa-regular fa-pen-to-square cursor-pointer" onClick={() => openEditModal(expense)}></i>
                    <Popconfirm
                      title="Are you sure you want to delete?"
                      onConfirm={() => deleteExpenseById(expense._id)}
                      okText="Yes"
                      cancelText="No"
                      >
                      <i className="fa-solid fa-trash-can"></i>
                    </Popconfirm>
                  </div>
                </div>
                <p className="text-gray-600">Amount: Rs. {expense.price.toFixed(2)}</p>
                <p className="text-gray-600">Date: {new Date(expense.date).toLocaleDateString()}</p>
                <p className="text-gray-600">Description: {expense.description}</p>
              </div>
              <div className="mt-4 sm:mt-0 sm:w-1/4 flex justify-center items-center flex-wrap gap-2">
  {expense.imagePath && expense.imagePath.length > 0 ? (
    expense.imagePath.map((image, index) => (
      <img 
        key={index} 
        src={image} 
                        alt={`Expense ${index + 1}`} 
        className="h-24 w-24 object-cover rounded-lg cursor-pointer"
        onClick={() => openModal(expense.imagePath[index] )}
      />
    ))
  ) : (
    <img 
      src={placeholderImage} 
      alt="Expense" 
      className="h-auto rounded-lg cursor-pointer"
    />
  )}
</div>
            </div>
          ))
        )}
      </div>

      {/* Modal for Image Preview */}
      {isModalOpen && (
        console.log("Modal Open with Image:", selectedImage),
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" onClick={closeModal}>
          <div 
            className="relative w-full sm:w-3/4 h-full sm:h-3/4 bg-white p-4 rounded-lg overflow-hidden scrollbar-hidden" 
            onClick={e => e.stopPropagation()}
          >
            <img 
              src={selectedImage ? selectedImage : placeholderImage} 
              alt="Selected"
              className="w-full h-full object-contain cursor-pointer"
              style={{ transform: 'scale(1)', cursor: 'zoom-in' }}
              ref={imgRef}
              onMouseDown={handleMouseDown}
              onClick={(e) => {
                const img = e.target;
                if (img.style.transform === 'scale(1)') {
                  img.style.transform = 'scale(2)';
                  img.style.cursor = 'zoom-out';
                } else {
                  img.style.transform = 'scale(1)';
                  img.style.cursor = 'zoom-in';
                }
                e.stopPropagation(); // Prevent click event from closing the modal
              }}
            />
            <button 
              onClick={closeModal} 
              className="absolute top-0 right-0 mt-2 mr-4 text-themeColor text-2xl font-bold"
            >
              &times;
            </button>
          </div>
        </div>
      )}
      {/* Modal for Editing Ledger */}
      {isEditModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg w-80">
          <div className="">
            <h2 className="text-xl font-semibold mb-4">Edit Expense</h2>
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Title</label>
              <input
                type="text"
                value={currentExpense.title}
                onChange={(e) => setCurrentExpense({ ...currentExpense, title: e.target.value })}
                className="border border-gray-300 rounded-md p-2 w-full"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Amount</label>
              <input
                type="text"
                value={currentExpense.price}
                onChange={(e) => setCurrentExpense({ ...currentExpense, price: e.target.value })}
                className="border border-gray-300 rounded-md p-2 w-full"
              />
            </div>
           
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Description</label>
              <textarea
                value={currentExpense.description}
                onChange={(e) => setCurrentExpense({ ...currentExpense, description: e.target.value })}
                className="border border-gray-300 rounded-md p-2 w-full"
              />
            </div>
            {/* Other fields like price, person, type, etc. */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Image</label>
              <input type="file" onChange={handleImageChange} className="border border-gray-300 rounded-md p-2" />
              {Array.isArray(currentExpense.imagePath) &&
                    currentExpense.imagePath.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Expense ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-md"
                      />
                    ))}
            </div>
            <div className="flex justify-end">
              <button className="bg-themeColor1 text-white px-4 py-2 rounded-md" onClick={updateExpense}>
                Save
              </button>
              <button className="ml-2 bg-gray-300 text-gray-800 px-4 py-2 rounded-md" onClick={closeEditModal}>
                Cancel
              </button>
            </div>
          </div>
          </div>
        </div>
      )}
   
      <ToastContainer />
    </div>
  );
};

export default CategoryDetail;
